import {
  EmptyState,
  FontSizeEnum,
  FontWeightEnum,
  IconSizeEnum,
  LoaderIcon,
  Typography,
} from '@acceso/design-system';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { BroadcasterScheduleProgramMiniCardBase } from '@/components/BroadcasterScheduleProgramMiniCardBase/BroadcasterScheduleProgramMiniCardBase';
import { TProgramBroadcast } from '@/features/broadcast-schedule/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useChannelsByIds } from '@/features/broadcasters/hooks/useChannelsByIds';
import { TSegment } from '@/features/segments/types';

type TBroadcastScheduleProgramBodySegmentCardListProps = {
  items: TSegment[];
  segments?: boolean;
  segmentsData?: UseInfiniteQueryResult<
    InfiniteData<
      {
        data: TSegment[];
        timestamp: string;
        offset: number;
        total: number;
      },
      unknown
    >,
    Error
  >;
  /**
   * The emission these blocks are listed under. Optional because the global segments
   * view mounts this same list for a channel, where there is no single emission.
   */
  programBroadcast?: TProgramBroadcast;
};

export function BroadcastScheduleProgramBodySegmentCardList({
  items,
  segments,
  segmentsData,
  programBroadcast,
}: TBroadcastScheduleProgramBodySegmentCardListProps) {
  const { activeSegment, isHeaderVisible } = useBroadcastScheduleProgram();
  const { channelsById } = useChannelsByIds(items.map(item => item.channel_id));
  const { t } = useTranslation();
  const observerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (
        entries[0].isIntersecting &&
        segmentsData?.hasNextPage &&
        !segmentsData?.isFetchingNextPage
      ) {
        segmentsData.fetchNextPage();
      }
    },
    [segmentsData?.hasNextPage, segmentsData?.isFetchingNextPage, segmentsData?.fetchNextPage]
  );

  useEffect(() => {
    if (!observerRef.current) return;

    const options = {
      root: scrollContainerRef.current,
      rootMargin: '0px 0px 400px 0px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver(handleIntersection, options);
    const currentObserverRef = observerRef.current;

    observer.observe(currentObserverRef);

    return () => {
      if (currentObserverRef) {
        observer.unobserve(currentObserverRef);
      }
      observer.disconnect();
    };
  }, [handleIntersection, items.length]);

  return (
    <div
      ref={scrollContainerRef}
      className={clsx(
        'flex min-h-[400px] flex-col overflow-y-auto',
        isHeaderVisible ? 'lg:max-h-[calc(100vh-280px)]' : 'lg:max-h-[calc(100vh-188px)]'
      )}
    >
      {items.map(item => (
        <BroadcasterScheduleProgramMiniCardBase
          key={item.id}
          item={item}
          channel={channelsById.get(item.channel_id)}
          segments={segments}
          isActive={item.id === activeSegment?.id}
          programBroadcast={programBroadcast}
        />
      ))}
      {items.length === 0 && <EmptyState title={t('No hay segmentos disponibles')} />}
      <div ref={observerRef} className="h-40" />

      {segmentsData?.isFetchingNextPage && (
        <div className="my-4 flex w-full items-center justify-center">
          <Typography weight={FontWeightEnum.Semibold} fontSize={FontSizeEnum.Sm}>
            <LoaderIcon size={IconSizeEnum['2XS']} />
          </Typography>
        </div>
      )}
    </div>
  );
}
